/**
 * Onboarding Questions Configuration
 *
 * Defines the order and content of onboarding questions.
 * Each item can be either an intro screen or a question.
 */

export const FINANCIAL_EXPERIENCE_OPTIONS = [
  {
    value: 'I haven\'t invested yet',
    label: 'I haven\'t invested yet',
    description: 'Ready to start your journey',
    image: 'seed.png'
  },
  {
    value: 'I\'ve just made my first investment',
    label: 'I\'ve just made my first investment',
    description: 'You\'ve taken the first step',
    image: 'seedling.png'
  },
  {
    value: 'Investing isn\'t new to me',
    label: 'Investing isn\'t new to me',
    description: 'You have some experience',
    image: 'growing-plant.png'
  },
  {
    value: 'I\'m actively managing investments',
    label: 'I\'m actively managing investments',
    description: 'You\'re experienced in investing',
    image: 'tree.png'
  }
];

export const MONEY_STAGE_OPTIONS = [
  { value: 'building', label: 'Building my foundations' },
  { value: 'growing', label: 'Growing my money' },
  { value: 'maximising', label: 'Maximising my nest-egg' }
];

export const MONTHLY_SAVINGS_OPTIONS = [
  {
    value: 0,
    label: 'I\'m not saving yet'
    // No image - blank card
  },
  {
    value: 1,
    label: 'Under $100/month',
    image: 'money-0.png'
  },
  {
    value: 2,
    label: '$100-$500/month',
    image: 'money-1.png'
  },
  {
    value: 3,
    label: '$500-$1,000/month',
    image: 'money-2.png'
  },
  {
    value: 4,
    label: '$1,000-$2,000/month',
    image: 'money-3.png'
  },
  {
    value: 5,
    label: '$2,000-$5,000/month',
    image: 'money-4.png'
  },
  {
    value: 6,
    label: 'Over $5,000/month',
    image: 'money-5.png'
  }
];

export const AGE_RANGE_OPTIONS = [
  {
    value: '18-29',
    label: '18-29'
  },
  {
    value: '30-39',
    label: '30-39'
  },
  {
    value: '40-49',
    label: '40-49'
  },
  {
    value: '50-59',
    label: '50-59'
  },
  {
    value: '60+',
    label: '60+'
  }
];

export const onboardingFlow = [
  {
    id: 'welcome-intro',
    type: 'intro',
    title: 'Welcome to Sophia',
    description: 'Let\'s get to know you better.\nSo we can provide you with a personalised investment coaching experience.',
  },
  {
    id: 'countries',
    type: 'question',
    field: 'countries',
    questionText: 'Which countries are relevant for your financial planning?',
    subheading: 'This helps me contextualize my responses',
    inputType: 'checkbox-multiple',
    options: [
      { value: 'Singapore', label: 'Singapore' },
      { value: 'Hong Kong', label: 'Hong Kong' },
      { value: 'United Kingdom', label: 'United Kingdom' },
      { value: 'Australia', label: 'Australia' },
      { value: 'United States', label: 'United States' }
    ],
    allowCustom: true,
    placeholder: 'Other countries...'
  },
  {
    id: 'financialExperience',
    type: 'question',
    field: 'financialExperience',
    questionText: 'What\'s your investment experience?',
    inputType: 'selection-cards',
    options: FINANCIAL_EXPERIENCE_OPTIONS
  },
  {
    id: 'mission-intro',
    type: 'intro',
    title: 'Define Your Goal',
    description: 'Having a clear objective is essential.\nSophia will help you move forward and achieve your financial goals.',
  },
  {
    id: 'focus-one-goal',
    type: 'intro',
    title: 'Focus on One Goal',
    description: 'Even if you have many financial goals, focusing on one at a time helps you actually achieve them.\n\nThink about the goal that feels most pressing right now.',
  },
  {
    id: 'currentMission',
    type: 'question',
    field: 'currentMission',
    questionText: 'What is your current financial goal?',
    inputType: 'goal-cards',
    options: [
      { value: 'Build emergency fund', label: 'Build emergency fund', image: 'build-emergency-fund.png' },
      { value: 'Pay off debt', label: 'Pay off debt', image: 'pay-off-debt.png' },
      { value: 'Start investing', label: 'Start investing', image: 'start-investing.png' },
      { value: 'Save for retirement', label: 'Save for retirement', image: 'save-for-retirement.png' }
    ],
    placeholder: 'Or describe your own goal...',
    allowCustom: true
  },
  // Plan approval screen - shows generated plan for user to accept/edit
  {
    id: 'plan-approval',
    type: 'plan-approval',
    // This screen waits for plan generation and displays it for approval
    // Only shown if currentMission was just answered in this session
  },
  // After plan approval, continue with demographic questions
  {
    id: 'moneyStage',
    type: 'question',
    field: 'moneyStage',
    questionText: 'What best describes where you are in your money journey? Select one.',
    inputType: 'checkbox-single', // Checkbox style but single selection
    options: MONEY_STAGE_OPTIONS
  },
  {
    id: 'monthlySavings',
    type: 'question',
    field: 'monthlySavings',
    questionText: 'How much are you saving right now per month? (US$)',
    subheading: 'This stays private and helps us set realistic goals',
    inputType: 'selection-cards',
    options: MONTHLY_SAVINGS_OPTIONS
  },
  {
    id: 'ageRange',
    type: 'question',
    field: 'ageRange',
    questionText: 'What\'s your age range?',
    subheading: 'Different life stages have different priorities',
    inputType: 'selection-cards',
    options: AGE_RANGE_OPTIONS
  },
  // After demographic questions, show engagement features
  {
    id: 'pwa-education',
    type: 'intro',
    title: 'Install Sophia',
    description: 'Get the best experience with quick access like a native app.',
    educationType: 'pwa' // Special flag for education screens
    // Available on all platforms: mobile (Add to Home Screen) and desktop (Install App button)
  },
  {
    id: 'notification-education',
    type: 'intro',
    title: 'Stay Connected to Your Goals',
    description: 'Let me explain how I can support you along the way.',
    educationType: 'notification' // Special flag for education screens
  },
  {
    id: 'contact-intro',
    type: 'intro',
    title: 'Optimize Your Experience',
    description: 'Think about when and how you\'d like Sophia to reach out.\nThis helps us make the most of our service for you.',
  },
  {
    id: 'contactInstructions',
    type: 'question',
    field: 'contactInstructions',
    questionText: 'When would you like to hear from Sophia?',
    inputType: 'quick-select-with-custom',
    placeholder: 'Or describe your own preference...',
    quickOptions: [
      { value: 'Every morning to start the day motivated', label: 'Every day in the morning' },
      { value: 'Every afternoon for a progress check', label: 'Every day in afternoon' },
      { value: 'Every other day at noon for consistent support', label: 'Every other day at noon' },
      { value: 'Twice a week in the late evening to reflect', label: 'Twice a week at late evening' },
      { value: 'Every weekend at noon for weekly planning', label: 'Every week at weekend noon' },
      { value: 'Every Friday at 3:00 PM for weekend prep', label: 'Every Friday at 3:00 PM' }
    ]
  },
  // Final loading screen - waits for todos to be created before proceeding to chat
  {
    id: 'todos-loading',
    type: 'todos-loading'
    // This screen polls the API and only proceeds when todos exist
  }
];

/**
 * Get the change-objective mini-flow (mission screens only)
 * Used when user changes their financial goal from chat
 * @returns {Array} - Array of mission-related onboarding items
 */
export function getChangeObjectiveFlow() {
  return [
    {
      id: 'mission-intro',
      type: 'intro',
      title: 'Define Your Goal',
      description: 'Having a clear objective is essential.\nSophia will help you move forward and achieve your financial goals.',
    },
    {
      id: 'focus-one-goal',
      type: 'intro',
      title: 'Focus on One Goal',
      description: 'Even if you have many financial goals, focusing on one at a time helps you actually achieve them.\n\nThink about the goal that feels most pressing right now.',
    },
    {
      id: 'currentMission',
      type: 'question',
      field: 'currentMission',
      questionText: 'What is your current financial goal?',
      inputType: 'goal-cards',
      options: [
        { value: 'Build emergency fund', label: 'Build emergency fund', image: 'build-emergency-fund.png' },
        { value: 'Pay off debt', label: 'Pay off debt', image: 'pay-off-debt.png' },
        { value: 'Start investing', label: 'Start investing', image: 'start-investing.png' },
        { value: 'Save for retirement', label: 'Save for retirement', image: 'save-for-retirement.png' }
      ],
      placeholder: 'Or describe your own goal...',
      allowCustom: true
    },
    {
      id: 'plan-approval',
      type: 'plan-approval',
    },
    {
      id: 'todos-loading',
      type: 'todos-loading'
    }
  ];
}

/**
 * Get questions that need to be answered based on user data
 * @param {Object} user - User object from Strapi
 * @returns {Array} - Array of onboarding items (intros and questions) that need to be shown
 */
export function getUnansweredQuestions(user) {
  const unansweredFields = [];

  // Check which fields are empty
  if (!user.countries) {
    unansweredFields.push('countries');
  }
  if (!user.financialExperience) {
    unansweredFields.push('financialExperience');
  }
  if (!user.currentMission) {
    unansweredFields.push('currentMission');
  }
  if (!user.moneyStage) {
    unansweredFields.push('moneyStage');
  }
  if (user.monthlySavings === null || user.monthlySavings === undefined) {
    unansweredFields.push('monthlySavings');
  }
  if (!user.ageRange) {
    unansweredFields.push('ageRange');
  }
  if (!user.contactInstructions) {
    unansweredFields.push('contactInstructions');
  }

  // If no unanswered questions, return empty array
  if (unansweredFields.length === 0) {
    return [];
  }

  // Build the flow: include intro screens before their corresponding questions
  const flowToShow = [];

  // Track if we're showing mission-related flow (for plan-approval and todos-loading)
  // Show if currentMission needs answering OR if we're showing any early onboarding questions
  let showMissionFlow = unansweredFields.includes('currentMission') ||
                        unansweredFields.includes('financialExperience');

  for (const item of onboardingFlow) {

    if (item.type === 'intro') {
      // For the welcome intro, only show if there are any unanswered questions
      if (item.id === 'welcome-intro') {
        if (flowToShow.length === 0) {
          // We'll add it only if we end up showing at least one question
          // Mark it for later addition
          continue;
        }
      } else {
        // For education intros (notification, pwa), always show after mission
        if (item.educationType) {
          // Check if we're in the mission flow
          if (showMissionFlow) {
            flowToShow.push(item);
          }
        } else {
          // For other intros, check if the next question in the flow needs to be answered
          const nextItem = onboardingFlow[onboardingFlow.indexOf(item) + 1];
          if (nextItem && nextItem.type === 'question' && unansweredFields.includes(nextItem.field)) {
            flowToShow.push(item);
          }
        }
      }
    } else if (item.type === 'plan-approval') {
      // Show plan approval if we're in the mission flow
      if (showMissionFlow) {
        flowToShow.push(item);
      }
    } else if (item.type === 'todos-loading') {
      // Only show todos-loading if we're in the mission flow (plan was just created)
      if (showMissionFlow) {
        flowToShow.push(item);
      }
    } else if (item.type === 'question' && unansweredFields.includes(item.field)) {
      // If this is the first item and we haven't added welcome-intro yet, add it first
      if (flowToShow.length === 0) {
        const welcomeIntro = onboardingFlow.find(i => i.id === 'welcome-intro');
        if (welcomeIntro) {
          flowToShow.push(welcomeIntro);
        }
      }
      flowToShow.push(item);
    }
  }

  return flowToShow;
}
